import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Sentry from "@sentry/react";
import { useAuth } from '../utils/useAuth';
import { useMyCapabilities } from '../utils/metadata/capabilities';
import { useTransientOverlay } from '../utils/transientOverlays';
import type { UseAuthReturn } from '../types/api';
import './ProfileMenu.css';

type CurrentView = 'Admin' | 'MainApp';

interface FeedbackIntegrationWithForm {
    createForm?: () => Promise<{
        appendToDom?: () => void;
        open?: () => void;
    } | null>;
}

export const ProfileMenu: React.FC = (): React.ReactElement => {
    const navigate = useNavigate();
    const location = useLocation();
    const { appUser, loading }: UseAuthReturn = useAuth();
    const { hasCapability } = useMyCapabilities();
    const currentView: CurrentView = location.pathname.split('/')[1] === 'admin' ? 'Admin' : 'MainApp';
    const [open, setOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const onLogout = useCallback((): void => {
        navigate('/auth/logout');
    }, [navigate]);

    const navigateToAdmin = useCallback((path = '/admin'): void => {
        navigate(path);
    }, [navigate]);

    const navigateToMainApp = useCallback((): void => {
        navigate('/');
    }, [navigate]);

    const openFeedback = useCallback((): void => {
        const feedback = Sentry.getFeedback();
        if (feedback) {
            feedback.createForm().then(form => {
                if (form) { form.appendToDom(); form.open(); }
            });
            return;
        }
        const client = Sentry.getClient();
        if (client) {
            const feedbackIntegration = client.getOptions().integrations.find(i => i.name === 'Feedback') as
                | FeedbackIntegrationWithForm
                | undefined;
            if (feedbackIntegration?.createForm) {
                feedbackIntegration.createForm().then((form) => {
                    if (form) {
                        if (typeof form.appendToDom === 'function') form.appendToDom();
                        if (typeof form.open === 'function') form.open();
                    }
                }).catch((err: unknown) => console.error('[Sentry] Error creating feedback form:', err));
            }
        }
    }, []);

    useTransientOverlay(open);

    // Close on outside click or Escape
    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    const displayName: string = appUser?.name || 'Account';
    const userEmail: string | undefined = appUser?.email;
    const canManageUsers = hasCapability('edit_permissions') || hasCapability('manage_users') || hasCapability('manage_openers');
    const initials = displayName
        .split(' ')
        .map(w => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    if (loading) {
        return (
            <div className="sky-profile">
                <div className="sky-profile-trigger">
                    <span className="sky-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                </div>
            </div>
        );
    }

    return (
        <div className="sky-profile" ref={menuRef}>
            <button className="sky-profile-trigger" onClick={() => setOpen(!open)}>
                <span className="sky-profile-avatar">{initials}</span>
                <span className="sky-profile-name">{displayName}</span>
                <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>

            {open && (
                <div className="sky-profile-dropdown">
                    {(displayName !== 'Account' || userEmail) && (
                        <div className="sky-profile-dropdown-header">
                            <div className="sky-profile-dropdown-name">{displayName}</div>
                            {userEmail && <div className="sky-profile-dropdown-email">{userEmail}</div>}
                        </div>
                    )}

                    {currentView === 'Admin' && (
                        <button className="sky-profile-dropdown-item" onClick={() => { navigateToMainApp(); setOpen(false); }}>
                            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /></svg>
                            Main App
                        </button>
                    )}

                    {currentView === 'MainApp' && (appUser?.is_super_admin || canManageUsers) && (
                        <button className="sky-profile-dropdown-item" onClick={() => { navigateToAdmin(appUser?.is_super_admin ? '/admin' : '/admin/users'); setOpen(false); }}>
                            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg>
                            {appUser?.is_super_admin ? 'Admin' : 'Users & Roles'}
                        </button>
                    )}

                    <button className="sky-profile-dropdown-item" onClick={() => { openFeedback(); setOpen(false); }}>
                        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
                        Give Feedback
                    </button>

                    <div className="sky-profile-dropdown-divider" />

                    <button className="sky-profile-dropdown-item danger" onClick={() => { onLogout(); setOpen(false); }}>
                        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                        Log out
                    </button>
                </div>
            )}
        </div>
    );
};

export default ProfileMenu;
